import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtMoneyShort, fmtNum } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  CalendarDays,
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  ArrowRight,
} from "lucide-react";

function fmtPeriod(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  } catch {
    return iso;
  }
}

function Delta({ current, prior, money = true, inverse = false }) {
  if (prior == null || prior === 0) return null;
  const diff = (current || 0) - prior;
  const pct = (diff / Math.abs(prior)) * 100;
  const isUp = diff > 0;
  const isFlat = Math.abs(pct) < 0.5;
  const isGood = inverse ? !isUp : isUp;
  const color = isFlat
    ? "text-slate-500 bg-slate-100"
    : isGood
    ? "text-emerald-700 bg-emerald-50"
    : "text-rose-700 bg-rose-50";
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${color}`}
      title={`Prior: ${money ? fmtMoneyShort(prior) : fmtNum(prior)}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.25} />
      {isFlat ? "flat" : `${Math.abs(pct).toFixed(1)}%`}
    </span>
  );
}

const TX_COLORS = {
  New: "bg-emerald-500",
  Renewal: "bg-blue-500",
  Endorsement: "bg-amber-500",
  Cancellation: "bg-rose-500",
  Reinstatement: "bg-purple-500",
};

export default function LatestProduction() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/latest-production")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <Card className="p-6 border-slate-200">
        <div className="text-sm text-slate-400">Loading latest production…</div>
      </Card>
    );
  }

  if (!data.latest_period) {
    return (
      <Card className="p-8 border-slate-200 text-center">
        <Sparkles className="w-8 h-8 mx-auto text-slate-300" strokeWidth={1.5} />
        <div className="mt-2 text-sm text-slate-500">No dated production yet.</div>
      </Card>
    );
  }

  const m = data.metrics || {};
  const pm = data.prior_metrics;
  const totalTxCount = (data.by_transaction_code || []).reduce((s, r) => s + r.count, 0) || 1;

  return (
    <Card className="p-6 border-slate-200" data-testid="latest-production-card">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} />
            Latest production
          </div>
          <h2 className="text-base font-semibold tracking-tight mt-0.5">
            Reporting period — {fmtPeriod(data.latest_period)}
          </h2>
          {data.prior_period && (
            <p className="text-[12px] text-slate-500 mt-0.5">
              Comparing against {fmtPeriod(data.prior_period)}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="latest-production-view-all"
          onClick={() => navigate("/policies")}
          className="text-[12px] text-blue-600 hover:underline inline-flex items-center gap-1"
        >
          View all policies <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="lp-metric-policies">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            Policies bound
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-xl font-semibold tabular-nums text-slate-900">{fmtNum(m.policies)}</div>
            {pm && <Delta current={m.policies} prior={pm.policies} money={false} />}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="lp-metric-gwp">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            Reported GWP
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-xl font-semibold tabular-nums text-slate-900">{fmtMoneyShort(m.gwp)}</div>
            {pm && <Delta current={m.gwp} prior={pm.gwp} />}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="lp-metric-mga">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            MGA net (prov)
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-xl font-semibold tabular-nums text-slate-900">{fmtMoneyShort(m.mga_net_prov)}</div>
            {pm && <Delta current={m.mga_net_prov} prior={pm.mga_net_prov} />}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="lp-metric-ar">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            AR opened
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <div className="text-xl font-semibold tabular-nums text-slate-900">{fmtMoneyShort(m.ar_outstanding)}</div>
            {pm && <Delta current={m.ar_outstanding} prior={pm.ar_outstanding} inverse />}
          </div>
        </div>
      </div>

      {/* Tx code mix + Segment mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">
            Transaction mix
          </div>
          <div className="flex h-2.5 rounded-full overflow-hidden ring-1 ring-slate-200" data-testid="lp-tx-bar">
            {(data.by_transaction_code || []).map((r) => (
              <div
                key={r.label}
                className={`${TX_COLORS[r.label] || "bg-slate-400"}`}
                style={{ width: `${Math.max(2, (r.count / totalTxCount) * 100)}%` }}
                title={`${r.label}: ${r.count} policies • ${fmtMoney(r.gwp)}`}
              />
            ))}
          </div>
          <ul className="mt-3 space-y-1.5">
            {(data.by_transaction_code || []).map((r) => (
              <li
                key={r.label}
                className="flex items-center justify-between text-[12px]"
                data-testid={`lp-tx-${r.label}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${TX_COLORS[r.label] || "bg-slate-400"}`} />
                  <span className="text-slate-700">{r.label}</span>
                  <span className="text-slate-400 tabular-nums">{fmtNum(r.count)}</span>
                </div>
                <span className="tabular-nums font-medium text-slate-900">{fmtMoney(r.gwp)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mb-2">
            Top segments this period
          </div>
          <ul className="space-y-2">
            {(data.by_segment || []).slice(0, 5).map((s) => {
              const max = (data.by_segment || [])[0]?.gwp || 1;
              const pct = Math.max(4, Math.round((s.gwp / max) * 100));
              return (
                <li key={s.label} data-testid={`lp-seg-${s.label}`} className="space-y-1">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-700 truncate">{s.label}</span>
                    <span className="tabular-nums font-medium text-slate-900">{fmtMoneyShort(s.gwp)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
            {(data.by_segment || []).length === 0 && (
              <li className="text-[12px] text-slate-400">No segment data.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Top insureds table */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
            Top insureds this period
          </div>
          <div className="text-[11px] text-slate-400">by reported GWP</div>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-4 py-2 font-medium w-8">#</th>
                <th className="px-4 py-2 font-medium">Insured</th>
                <th className="px-4 py-2 font-medium">Carrier</th>
                <th className="px-4 py-2 font-medium">Segment</th>
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 font-medium text-right">Policies</th>
                <th className="px-4 py-2 font-medium text-right">GWP</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_insureds || []).map((r, i) => (
                <tr
                  key={`${r.insured_name}-${i}`}
                  data-testid={`lp-insured-${i}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                >
                  <td className="px-4 py-2.5 text-[11px] text-slate-400 tabular-nums">{i + 1}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900 truncate max-w-[280px]" title={r.insured_name}>
                    {r.insured_name}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{r.carrier || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.segment || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.state || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{fmtNum(r.count)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">
                    {fmtMoney(r.gwp)}
                  </td>
                </tr>
              ))}
              {(data.top_insureds || []).length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-400 text-sm">
                    No production in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
